import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, Eye, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultRules = {
  min_account_age_days: 14,
  never_logged_in_after_days: 30,
  inactive_after_days: null,
  require_zero_content: true,
  exclude_emails: [],
};

/**
 * Bulk-removal rules for stale/spam accounts.
 *
 * Preview always reflects the form's current (possibly unsaved) values, but
 * "Run Pruning" acts on whatever is saved on the server - never on unsaved
 * form state - so there's no way to delete against rules you haven't
 * actually committed to. The confirm dialog is gated on having previewed
 * the SAVED rules at least once in this session, so a delete can't happen
 * without having seen a concrete list first.
 */
const AdminUserPruning = () => {
  const [rules, setRules] = useState(defaultRules);
  const [savedRules, setSavedRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [excludeText, setExcludeText] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const headers = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/user-pruning/settings`, { headers: headers() });
        setRules(res.data);
        setSavedRules(res.data);
        setExcludeText((res.data.exclude_emails || []).join('\n'));
      } catch (error) {
        toast({ title: 'Failed to load rules', description: error.response?.data?.detail || 'Please try again.', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentPayload = () => ({
    ...rules,
    exclude_emails: excludeText.split('\n').map((s) => s.trim()).filter(Boolean),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = currentPayload();
      const res = await axios.put(`${API}/admin/user-pruning/settings`, payload, { headers: headers() });
      setRules(res.data);
      setSavedRules(res.data);
      toast({ title: 'Rules saved', description: '"Run Pruning" will use these rules.' });
    } catch (error) {
      toast({ title: 'Save failed', description: error.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const payload = currentPayload();
      const res = await axios.post(`${API}/admin/user-pruning/preview`, payload, { headers: headers() });
      setPreview(res.data);
    } catch (error) {
      toast({ title: 'Preview failed', description: error.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/user-pruning/run`, {}, { headers: headers() });
      toast({ title: 'Pruning complete', description: `${res.data.deleted_count} account(s) deleted.` });
      setPreview(null);
      setConfirmOpen(false);
    } catch (error) {
      toast({ title: 'Run failed', description: error.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  const rulesMatchSaved = savedRules && JSON.stringify(currentPayload()) === JSON.stringify(savedRules);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-red-600" />
          User Pruning
        </h1>
        <p className="text-gray-500">
          Bulk-remove stale or spam accounts on your own terms. Nothing runs automatically - rules only
          take effect when you click Run Pruning, and deletion is permanent.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rules</CardTitle>
          <CardDescription>Staff, admin and super admin accounts are never eligible, regardless of these settings.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Minimum account age (days)</Label>
              <Input
                type="number"
                min={0}
                value={rules.min_account_age_days}
                onChange={(e) => setRules({ ...rules, min_account_age_days: parseInt(e.target.value, 10) || 0 })}
                data-testid="pruning-min-age"
              />
              <p className="text-xs text-gray-500 mt-1">Accounts newer than this are never touched.</p>
            </div>

            <div>
              <Label>Require zero QR codes/projects</Label>
              <div className="flex items-center gap-2 mt-2.5">
                <Switch
                  checked={rules.require_zero_content}
                  onCheckedChange={(checked) => setRules({ ...rules, require_zero_content: checked })}
                  data-testid="pruning-require-zero-content"
                />
                <span className="text-sm text-gray-600">{rules.require_zero_content ? 'On' : 'Off'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Skip anyone who's actually created something.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <Label>Never logged in, after (days)</Label>
                <Switch
                  checked={rules.never_logged_in_after_days !== null}
                  onCheckedChange={(checked) => setRules({ ...rules, never_logged_in_after_days: checked ? 30 : null })}
                  data-testid="pruning-never-logged-in-toggle"
                />
              </div>
              {rules.never_logged_in_after_days !== null && (
                <Input
                  type="number"
                  min={0}
                  className="mt-2"
                  value={rules.never_logged_in_after_days}
                  onChange={(e) => setRules({ ...rules, never_logged_in_after_days: parseInt(e.target.value, 10) || 0 })}
                  data-testid="pruning-never-logged-in-days"
                />
              )}
              <p className="text-xs text-gray-500 mt-1">Registered but has never logged in at all.</p>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Inactive since last login (days)</Label>
                <Switch
                  checked={rules.inactive_after_days !== null}
                  onCheckedChange={(checked) => setRules({ ...rules, inactive_after_days: checked ? 180 : null })}
                  data-testid="pruning-inactive-toggle"
                />
              </div>
              {rules.inactive_after_days !== null && (
                <Input
                  type="number"
                  min={0}
                  className="mt-2"
                  value={rules.inactive_after_days}
                  onChange={(e) => setRules({ ...rules, inactive_after_days: parseInt(e.target.value, 10) || 0 })}
                  data-testid="pruning-inactive-days"
                />
              )}
              <p className="text-xs text-gray-500 mt-1">Has logged in before, just not for a long time.</p>
            </div>
          </div>

          <div>
            <Label>Never prune these emails</Label>
            <Textarea
              rows={3}
              placeholder="one@example.com&#10;two@example.com"
              value={excludeText}
              onChange={(e) => setExcludeText(e.target.value)}
              data-testid="pruning-exclude-emails"
            />
            <p className="text-xs text-gray-500 mt-1">One per line. Always protected even if every rule above matches.</p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="pruning-save-rules">
              {saving ? 'Saving...' : 'Save Rules'}
            </Button>
            <Button variant="outline" onClick={handlePreview} disabled={previewing} data-testid="pruning-preview">
              <Eye className="w-4 h-4 mr-2" />
              {previewing ? 'Checking...' : 'Preview Matches'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardHeader>
            <CardTitle>{preview.count} account{preview.count === 1 ? '' : 's'} match{preview.count === 1 ? 'es' : ''} these rules</CardTitle>
            <CardDescription>
              {rulesMatchSaved
                ? 'These rules are saved - Run Pruning below will delete exactly this list.'
                : 'You have unsaved changes - save the rules first, Run Pruning always acts on the saved version.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {preview.count > 0 ? (
              <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Name</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Email</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Age (days)</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Last login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.candidates.map((c) => (
                      <tr key={c.id} className="border-t">
                        <td className="py-2 px-3">{c.name}</td>
                        <td className="py-2 px-3 text-gray-600">{c.email}</td>
                        <td className="py-2 px-3">{c.age_days}</td>
                        <td className="py-2 px-3 text-gray-500">{c.last_login_at ? new Date(c.last_login_at).toLocaleDateString() : 'Never'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Nobody currently matches these rules.</p>
            )}

            {preview.count > 0 && (
              <Button
                className="mt-4 bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setConfirmOpen(true)}
                disabled={!rulesMatchSaved}
                data-testid="pruning-run-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Run Pruning
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {preview?.count} account{preview?.count === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the saved rules' current matches right now. It cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={running}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRun}
              disabled={running}
              className="bg-red-600 hover:bg-red-700"
              data-testid="pruning-confirm-delete"
            >
              {running ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUserPruning;
